/* eslint-disable no-case-declarations */
import * as React from "react";
import { Dropdown } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { pdfViewerStore } from "Stores/PdfViewerStore";
import { wopiViewerStore } from "Stores/WOPIViewerStore";
import { onlineViewerStore } from "Stores/OnlineViewerStore";
import { withModal, WithModal } from "Components/General/Modal/withModal";
import { notificationError } from "utils/libHelpers/AntdNotification";
import { EmailSendModal } from "Pages/Project/Communication/EmailSendModal/EmailSendModal";
import { emailSendModal } from "Pages/Project/Communication/utils/emailModalProps";
import { viewsStore } from "../Stores/ViewsStore";
import { BCFViewerModal } from "Components/BcfViewer/BCFViewerModal";
import { modalProps } from "Pages/Project/QA/utils/modalProps";
import { xktViewerStore } from "Components/XKTViewer/Stores/XKTViewerStore";
import { collectionFolderStore } from "Pages/Project/Documents/CollectionFolder/CollectionFolderStore";
import { StartWorkflowWithFiles } from "Pages/Project/Documents/CollectionFolder/StartWorkflowWithFiles";
import { ItemType } from "antd/lib/menu/interface";
import { PrintForm } from "Components/FilePrint/Components/PrintForm";
import { useCallback, useMemo } from "react";
import { useEmailAttachments } from "utils/hooks/useEmailAttachments";
import { emailSubmitStore } from "Pages/Project/Communication/Stores/EmailSubmitStore";
import { fileDownloadStore } from "Pages/Project/Documents/FileDownload/FileDownloadStore";
import { apryseViewerStore } from "Stores/PdfViewerStore/ApryseViewerStore";

interface ViewsTableRowActionsProps extends WithModal {
  data: API_FileDataEntity | API_FolderDataEntity;
  children: React.ReactNode;
  showOneFileActions?: boolean;
  showBasicFileActions?: boolean;
  showModuleFileActions?: boolean;
  showFolderActions?: boolean;
}

export const ViewsTableRowActions = withModal(
  observer(
    ({
      data,
      children,
      showOneFileActions = true,
      showBasicFileActions = true,
      showModuleFileActions = true,
      showFolderActions = false,
      showModal,
    }: ViewsTableRowActionsProps) => {
      const { t } = useTranslation(I18N_NS.PROJECT);
      const { checkAttachmentsAccess } = useEmailAttachments(emailSubmitStore);

      const doFileClick = useCallback(
        (key: string, data: API_FileDataEntity) => {
          const isRWMright = !data.fileActions.download && data.fileActions.downloadWithWatermark;
          switch (key) {
            // File actions START
            case "bcf_view":
              showModal(BCFViewerModal, {
                modalProps: {
                  ...modalProps,
                  maskClosable: true,
                  width: "100%",
                  title: `${t("File")}: ${data.name}`,
                  style: { maxWidth: "1300px" },
                  className: "full-height-modal with-drawer bcf-modal",
                  zIndex: 1013,
                },
                componentProps: { fileId: data.id },
              });
              break;
            case "xkt_view":
              xktViewerStore.initViewer({
                fileName: data.name,
                fileId: data.id as number,
                parentFolderId: data.parentId,
              });
              break;
            case "apryse_view":
              apryseViewerStore.fetch(data.id, {
                fileName: data.name,
                canDownload: data.fileActions.download || data.fileActions.downloadWithWatermark,
              });
              break;
            case "easy_view":
              pdfViewerStore.fetch(data.id, {
                fileName: data.name,
                canDownload: data.fileActions.download || data.fileActions.downloadWithWatermark,
              });
              break;
            case "wopi_view":
              wopiViewerStore.fetch(data.id, { fileName: data.name });
              break;
            case "wopi_edit":
              wopiViewerStore.fetch(data.id, { fileName: data.name, edit: data.viewers.office365Edit });
              break;
            case "online_view":
              onlineViewerStore.fetch(data.id, { fileName: data.name });
              break;
            case "download":
              let downloadRequest: API_FileRequest[];
              if (data.state === "SYMLINK") {
                downloadRequest = [{ id: data.linkId, state: data.state }];
              } else if (data.state == "EXTERN_SYMLINK") {
                downloadRequest = [{ id: data.id, state: data.state, linkId: data.linkId }];
              } else {
                downloadRequest = [{ id: data.id, state: data.state }];
              }
              fileDownloadStore.initModal(downloadRequest, isRWMright ? "RWM" : "ALL");
              break;
            case "print":
              showModal(PrintForm, {
                modalProps: { style: { maxWidth: 1300, top: 20 }, width: "100%", footer: null, title: " " },
                componentProps: {
                  files: [data],
                },
              });
              break;
            case "send":
              checkAttachmentsAccess(viewsStore.getFilesFromIds([data.id]));
              showModal(EmailSendModal, {
                modalProps: { ...emailSendModal },
                componentProps: {
                  mailType: "attach",
                },
              });
              break;
            case "collection_folder":
              collectionFolderStore.addFile(data as API_FileDataEntity);
              break;
            case "start_workflow":
              showModal(StartWorkflowWithFiles, {
                modalProps: { ...modalProps, title: t("Start workflow") },
                componentProps: { fileIds: [data.id], startFrom: "VIEW" },
              });
              break;
            default:
              notificationError("Error", "Unknown action" + key);
              break;
          }
        },
        [showModal]
      );

      const handleMenuClick = useCallback(
        ({
          key,
          domEvent,
        }: {
          key: string;
          domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>;
        }) => {
          domEvent.preventDefault();
          domEvent.stopPropagation();
          doFileClick(key, data as API_FileDataEntity);
        },
        [data, doFileClick]
      );

      const menuItems = useMemo<ItemType[]>(() => {
        const fileData = data as API_FileDataEntity;
        const oneFileItems: ItemType[] = [];
        const basicItems: ItemType[] = [];
        const moduleItems: ItemType[] = [];

        if (fileData.viewers) {
          if (fileData.viewers.bcfViewer) {
            oneFileItems.push({ key: "bcf_view", label: t("Open with BCF Viewer") });
          }
          if (fileData.viewers.bimViewerSupported) {
            oneFileItems.push({
              key: "xkt_view",
              label: (
                <>
                  <FontAwesomeIcon icon={["far", "cube"]} fixedWidth={true} className="mr-5" />
                  {fileData.viewers.bimViewer ? t("Open with BIM Viewer") : t("BIM Viewer (being processed)")}
                </>
              ),
            });
          }
          if (fileData.viewers.webViewer) {
            oneFileItems.push({ key: "apryse_view", label: t("View with WebViewer") });
          } else if (fileData.viewers.easyViewer) {
            oneFileItems.push({ key: "easy_view", label: t("View with Easy Viewer") });
          } else if (fileData.viewers.office365) {
            oneFileItems.push({ key: "wopi_view", label: t("View with Office Online") });
            if (fileData.viewers.office365Edit) {
              oneFileItems.push({ key: "wopi_edit", label: t("Edit with Office Online") });
            }
          } else if (fileData.viewers.onlineViewer) {
            oneFileItems.push({ key: "online_view", label: t("View with Online Viewer") });
          }
        }

        if (fileData.fileActions && (fileData.fileActions.download || fileData.fileActions.downloadWithWatermark)) {
          basicItems.push({
            key: "download",
            label: (
              <>
                <FontAwesomeIcon icon={["far", "download"]} fixedWidth={true} className="mr-5" />
                {t("Download")}
              </>
            ),
          });
        }

        if (fileData.fileActions?.collectionFolder) {
          basicItems.push({
            key: "collection_folder",
            label: (
              <>
                <FontAwesomeIcon icon={["far", "folder-open"]} fixedWidth={true} className="mr-5" />
                {t("Add to collection folder")}
              </>
            ),
          });
        }

        if (fileData.fileActions?.print) {
          basicItems.push({
            key: "print",
            label: (
              <>
                <FontAwesomeIcon icon={["far", "print"]} fixedWidth={true} className="mr-5" />
                {t("Print job")}...
              </>
            ),
          });
        }

        if (fileData.state !== "DELETED") {
          if (fileData.fileActions?.send) {
            moduleItems.push({
              key: "send",
              label: (
                <>
                  <FontAwesomeIcon icon={["far", "envelope"]} fixedWidth={true} className="mr-5" />
                  {t("Send")}...
                </>
              ),
            });
          }
        }

        if (fileData.fileActions?.workflow) {
          moduleItems.push({
            key: "start_workflow",
            label: (
              <>
                <FontAwesomeIcon icon={["far", "code-branch"]} fixedWidth={true} className="mr-5" />
                {t("Start workflow")}...
              </>
            ),
          });
        }

        return [
          showOneFileActions &&
            oneFileItems.length > 0 && {
              type: "group" as const,
              children: oneFileItems,
              label: t("This file"),
            },
          showBasicFileActions &&
            basicItems.length > 0 && {
              type: "group" as const,
              children: basicItems,
              label: t("Basics"),
            },
          showModuleFileActions &&
            moduleItems.length > 0 && {
              label: t("Other modules"),
              type: "group" as const,
              children: moduleItems,
            },
        ].filter(Boolean) as ItemType[];
      }, [data, showOneFileActions, showBasicFileActions, showModuleFileActions]);

      if (menuItems.length === 0) {
        return <span className="color-pale-grey">{children}</span>;
      }

      return (
        <Dropdown
          menu={{ items: menuItems, onClick: handleMenuClick, className: "documents-actions" }}
          trigger={["click"]}
        >
          {children}
        </Dropdown>
      );
    }
  )
);
